import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { IconApartmentBuildingOutline18 } from 'nucleo-ui-outline-18'
import { OverlayProvider } from 'overlay-kit'
import { lazy, Suspense, useEffect, useRef } from 'react'
import { BrandLoader } from '~/components/brand/brand-loader'
import { CelebrationHost } from '~/components/celebration/celebration-host'
import { CommandPalette } from '~/components/command-palette/command-palette'
import { UploadTray } from '~/components/files/upload-tray'
import { IconRail } from '~/components/layout/icon-rail'
import { MobileNav } from '~/components/layout/mobile-nav'
import { SideNav } from '~/components/layout/side-nav'
import { Sidebar, WorkspaceSidebarHeader } from '~/components/layout/sidebar'
import { GettingStartedChecklist } from '~/components/onboarding/getting-started-checklist'
import { ProjectCreatePanel } from '~/components/projects/project-create-panel'
import { TaskPanel } from '~/components/tasks/task-panel'
import { TourController } from '~/components/tour/tour-controller'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { useImpersonationSession } from '~/lib/impersonation'
import { optionalBoolean, optionalString } from '~/lib/search-params'
import type { Workspace } from '~/lib/workspace-context'
import { WorkspaceProvider } from '~/lib/workspace-provider'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

const VisitPanel = lazy(async () => {
  const module = await import('~/components/visits/visit-panel')
  return { default: module.VisitPanel }
})

export const Route = createFileRoute('/_authenticated/$workspaceSlug')({
  component: WorkspaceLayout,
  // `task` opens the docked task panel over whichever page is active, so the
  // param lives here on the layout — every child route inherits it. Same for
  // `newProject`, which opens the docked project-create panel.
  validateSearch: (
    search: Record<string, unknown>
  ): {
    task?: Id<'tasks'>
    visit?: Id<'siteVisits'>
    newProject?: boolean
  } => ({
    newProject: optionalBoolean(search.newProject),
    task: optionalString<Id<'tasks'>>(search.task),
    visit: optionalString<Id<'siteVisits'>>(search.visit),
  }),
})

/**
 * Records the workspace as the caller's last-visited one (the `/home`
 * landing target), once per workspace id per mount.
 */
const TouchLastVisited = ({ workspaceId }: { workspaceId: Workspace['_id'] }) => {
  const touch = useConvexMutation(api.workspaces.touchLastVisited)
  const touchedRef = useRef<Workspace['_id'] | null>(null)
  useEffect(() => {
    if (touchedRef.current === workspaceId) {
      return
    }
    touchedRef.current = workspaceId
    void touch({ workspaceId }).catch(() => {
      // A redirect hint only — losing one touch is harmless.
    })
  }, [touch, workspaceId])
  return null
}

/**
 * Resolves the URL's `$workspaceSlug` to a workspace and renders the app
 * shell around it. The resolution is a live subscription, so losing
 * membership mid-session reactively swaps the shell for the not-found state,
 * unmounting every workspace-scoped query below.
 */
function WorkspaceLayout() {
  const { t } = useLingui()
  const { workspaceSlug } = Route.useParams()
  const { isAuthenticated } = useConvexAuth()
  // Held until the session is server-verified, and not merely for tidiness:
  // `getBySlug` answers `null` for a caller it cannot see a membership for,
  // which is indistinguishable from "no such workspace". Asking it while the
  // socket is still unauthenticated would render the not-found state over a
  // workspace that exists, for as long as the handshake takes. The gate above
  // renders this shell optimistically, so that window is real.
  const { data: workspace } = useQuery({
    ...convexQuery(api.workspaces.getBySlug, { slug: workspaceSlug }),
    enabled: isAuthenticated,
  })
  const { isImpersonating, isResolved } = useImpersonationSession()

  if (workspace === null) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <EmptyState
          description={t`This workspace doesn't exist or you don't have access to it.`}
          icon={IconApartmentBuildingOutline18}
          title={t`Workspace not found`}
        >
          <Button render={<Link to="/home" />}>
            <Trans>Go to my workspace</Trans>
          </Button>
        </EmptyState>
      </div>
    )
  }

  // App shell: grey backdrop, one docked sidebar carrying all navigation, and
  // a floating white card holding nothing but the routed page content.
  // `OverlayProvider` wraps `<main>` so a docked panel renders as its flex
  // sibling — opening one reflows the content rather than covering it.
  // Below `lg` the sidebar collapses into `MobileNav`'s hamburger drawer, and
  // the card spans the full width with its own left padding (the sidebar
  // normally supplies that gap on desktop).
  //
  // The shell is deliberately rendered BEFORE the slug resolves. None of this
  // chrome depends on the workspace document — the nav links are built from
  // the slug, which is already in the URL — so gating it on `getBySlug` only
  // ever traded a laid-out screen for a full-page spinner, one round trip
  // later. Everything genuinely workspace-scoped stays behind `isReady`.
  const isReady = workspace !== undefined

  return (
    <WorkspaceProvider workspace={workspace}>
      <div className="app-workspace-shell flex h-full bg-canvas">
        <div className="app-workspace-navigation hidden shrink-0 lg:flex">
          <IconRail />
          <Sidebar footer={<GettingStartedChecklist />} header={<WorkspaceSidebarHeader />}>
            <SideNav />
          </Sidebar>
        </div>
        <div className="app-workspace-canvas min-w-0 flex-1 py-2 pr-2 pl-2 lg:pl-0">
          <div className="app-workspace-card relative flex h-full flex-col overflow-hidden rounded-md bg-background shadow-card">
            <MobileNav />
            <OverlayProvider>
              {/* `min-h-0` is load-bearing, not defensive. A column flex item
                  defaults to `min-height: auto`, which refuses to shrink below
                  its content — so on any page taller than the viewport this
                  wrapper grew to the full content height, `main` (`flex-1`)
                  grew with it, and `main` therefore never had anything to
                  scroll. The card's `overflow-hidden` then clipped the
                  overflow, and the page simply ended mid-content with no way
                  to reach the rest. Settings showed it worst because its own
                  inner scroll container sizes off `main`. */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <main className="app-workspace-main min-h-0 min-w-0 flex-1 overflow-y-auto">
                  {isReady ? (
                    <Outlet />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <BrandLoader />
                    </div>
                  )}
                </main>
              </div>
            </OverlayProvider>
            {/* The docked panels read the workspace, so they wait for it —
                unlike the chrome above, which is slug-only. */}
            {isReady ? (
              <>
                {/* The docked task panel overlays the card's right edge
                    whenever the `task` search param holds an id. */}
                <TaskPanel />
                {/* Rich visit reports use the same docked interaction model;
                    the full page stays one explicit expand action away. */}
                <Suspense fallback={null}>
                  <VisitPanel />
                </Suspense>
                {/* Same treatment for project creation — `newProject=true`
                    docks the create panel over whichever page is active. */}
                <ProjectCreatePanel />
              </>
            ) : null}
          </div>
        </div>
        {/* The upload tray floats bottom-right. `z-20` is the BOTTOM rung of
            the floating scale — docked panels (z-30), the tour (z-40) and
            dialogs/menus (z-50) all cover it. It must NOT join the z-50 tier
            the rest of the overlay layer uses: this is a plain in-tree sibling
            of the card, so at z-50 it outranked the docked panels above and sat
            over the project panel's Create button. Dialogs were unaffected
            either way — Base UI portals them to `<body>`, which wins the z-50
            tie on DOM order. */}
        <div className="pointer-events-none fixed right-4 bottom-4 z-20 flex flex-col items-end gap-2">
          <UploadTray />
        </div>
        {/* Stamps `activeWorkspaceId` and `lastActiveAt` purely from mounting.
            Under staff impersonation that would move where the target lands
            next sign-in and make a dormant account read as active in the admin
            surface *because* someone looked at it. The server no-ops it too;
            not mounting avoids the pointless round trip. */}
        {isReady && isResolved && !isImpersonating ? (
          <TouchLastVisited workspaceId={workspace._id} />
        ) : null}
        {isReady ? <TourController /> : null}
        {/* ⌘K from anywhere under the workspace. Behind `isReady` because
            every command it offers is workspace-scoped; the binding itself
            costs nothing until the palette is first opened. */}
        {isReady ? <CommandPalette /> : null}
        <CelebrationHost />
      </div>
    </WorkspaceProvider>
  )
}
